import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Public business landing page at the CRM root URL.
 *
 * This page is REQUIRED for Twilio A2P 10DLC carrier review.
 * When carriers (TCR, AT&T, T-Mobile, Verizon...) visit
 * https://crm.iqeslowvoltage.com they must find a publicly accessible
 * page that identifies the business and links to the compliance pages.
 * Without this, A2P campaign submissions are rejected with error 30921
 * "Website requires authentication and cannot be reviewed".
 */
export default function PublicLandingPage() {
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800" data-testid="public-landing-page">
      {/* HEADER */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-blue-600 text-white flex items-center justify-center font-extrabold text-lg shadow-sm">IQ</div>
            <div>
              <div className="font-extrabold text-slate-900 leading-tight">IQES Low Voltage Solutions</div>
              <div className="text-xs text-slate-500">BICSI Standards</div>
            </div>
          </div>
          <Link
            to="/login"
            data-testid="public-signin-link"
            className="px-5 py-2.5 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition"
          >
            Sign in to CRM →
          </Link>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-10">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-bold tracking-wider uppercase mb-4">
              Commercial Low Voltage Specialists
            </div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 leading-tight">
              Structured Cabling, Security &amp; Communications
            </h1>
            <p className="mt-5 text-lg text-slate-600 leading-relaxed">
              IQES Low Voltage Solutions designs, installs and certifies low‑voltage systems
              for commercial, hospitality and government projects across Florida.
              Fiber optics, Cat 6/6A data, access control, CCTV, fire alarm cabling,
              network design and certification &mdash; performed by BICSI‑trained technicians
              under Florida-registered business.
            </p>

            <div className="mt-7 flex flex-wrap gap-3">
              <a
                href="tel:7864367307"
                className="px-5 py-3 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition shadow-sm"
                data-testid="landing-call-btn"
              >
                Call (786) 981‑3760
              </a>
              <a
                href="mailto:info@iqeslowvoltage.com"
                className="px-5 py-3 rounded-lg bg-white border border-slate-300 text-slate-800 font-semibold hover:bg-slate-50 transition"
                data-testid="landing-email-btn"
              >
                info@iqeslowvoltage.com
              </a>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-7">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-4">Service Portfolio</h3>
            <ul className="space-y-3 text-sm text-slate-700">
              <li className="flex gap-2"><span className="text-blue-600 font-bold">•</span> Fiber optic cabling &amp; certification (SM/MM)</li>
              <li className="flex gap-2"><span className="text-blue-600 font-bold">•</span> Structured data cabling (Cat 5e / 6 / 6A / 7)</li>
              <li className="flex gap-2"><span className="text-blue-600 font-bold">•</span> Access control: readers, controllers, electric locks, intercom</li>
              <li className="flex gap-2"><span className="text-blue-600 font-bold">•</span> CCTV / IP video surveillance &amp; NVR</li>
              <li className="flex gap-2"><span className="text-blue-600 font-bold">•</span> Wi‑Fi design, surveys and optimization</li>
              <li className="flex gap-2"><span className="text-blue-600 font-bold">•</span> Project management Div. 27 &amp; Div. 28</li>
            </ul>
            <div className="mt-5 pt-5 border-t border-slate-100 text-xs text-slate-500">
              Headquarters: 255 S Orange Avenue, Suite 104 #1905, Orlando, FL 32801
            </div>
          </div>
        </div>
      </section>

      {/* COMMUNICATIONS COMPLIANCE BLOCK (this is what Twilio / TCR look for) */}
      <section className="max-w-6xl mx-auto px-6 py-10">
        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
          <h2 className="text-2xl font-extrabold text-slate-900">Customer Communications</h2>
          <p className="mt-2 text-slate-600">
            We communicate with our customers and prospects exclusively about active services.
            All recipients have explicitly opted in through our website form, a recorded phone call,
            or by signing a service authorization document.
          </p>

          <div className="grid md:grid-cols-3 gap-4 mt-7">
            <a
              href="/sms-optin.html"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-blue-50 border border-blue-200 rounded-xl p-5 hover:bg-blue-100 transition"
              data-testid="landing-link-sms-optin"
            >
              <div className="text-xs font-bold uppercase tracking-wider text-blue-700 mb-2">SMS Program</div>
              <div className="font-semibold text-slate-900">SMS Sign‑up &amp; Opt‑in</div>
              <p className="text-xs text-slate-600 mt-1">
                Subscribe to appointment confirmations, technician ETAs and service updates.
                Msg &amp; data rates may apply. Reply STOP to opt out.
              </p>
            </a>

            <a
              href="/privacy-policy.html"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-slate-50 border border-slate-200 rounded-xl p-5 hover:bg-slate-100 transition"
              data-testid="landing-link-privacy"
            >
              <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Privacy</div>
              <div className="font-semibold text-slate-900">Privacy Policy</div>
              <p className="text-xs text-slate-600 mt-1">
                We do not sell, rent or share mobile phone numbers or SMS opt‑in data with
                any third party for marketing purposes.
              </p>
            </a>

            <a
              href="/terms.html"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-slate-50 border border-slate-200 rounded-xl p-5 hover:bg-slate-100 transition"
              data-testid="landing-link-terms"
            >
              <div className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2">Legal</div>
              <div className="font-semibold text-slate-900">Terms of Service</div>
              <p className="text-xs text-slate-600 mt-1">
                Full terms governing our services and our SMS notification program.
              </p>
            </a>
          </div>

          <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            <strong>SMS Program — Voluntary Opt-in Disclosure:</strong> Subscribing to our SMS program is
            {' '}<strong>100% voluntary and is NOT a condition of any purchase, service, employment or business relationship</strong>
            {' '}with IQES Low Voltage Solutions. We send SMS messages only to customers and prospects who have
            explicitly opted in. Message frequency varies (typically 1–4 messages per service event).
            {' '}<strong>Message and data rates may apply.</strong>
            Reply <code className="px-1.5 py-0.5 bg-white rounded border">HELP</code> for help or
            {' '}<code className="px-1.5 py-0.5 bg-white rounded border">STOP</code> to opt out at any time at no cost.
            We do not share mobile information with third parties for marketing purposes.
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-8 text-center text-sm text-slate-500">
          <div className="font-bold text-slate-700 mb-1">IQES Low Voltage Solutions LLC</div>
          255 S Orange Avenue, Suite 104 #1905, Orlando, FL 32801 · <a href="tel:7864367307" className="text-blue-600 hover:underline">786‑981‑3760</a> · <a href="mailto:info@iqeslowvoltage.com" className="text-blue-600 hover:underline">info@iqeslowvoltage.com</a>
          <div className="mt-2 text-xs">
            © 2026 IQES Low Voltage Solutions LLC. All rights reserved.
          </div>
          <div className="mt-3 text-xs">
            <a href="/privacy-policy.html" className="text-slate-500 hover:text-blue-600">Privacy Policy</a>
            {' · '}
            <a href="/terms.html" className="text-slate-500 hover:text-blue-600">Terms of Service</a>
            {' · '}
            <a href="/sms-optin.html" className="text-slate-500 hover:text-blue-600">SMS Sign‑up</a>
            {' · '}
            <Link to="/login" className="text-slate-500 hover:text-blue-600">CRM Login</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
